import { Box, IconButton } from '@mui/material';
import { Spacer } from 'src/components/new/Spacer';
import { Typography } from 'src/components/new/Typography';
import { UserSignature } from 'src/utils/constant';
import { CurrentUser } from 'src/features/user/constants';
import StarIcon from '@mui/icons-material/Star';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

interface Props {
  signature: UserSignature;
  onMakeDefaultClick: (signature: UserSignature) => void;
  onEditClick: (signature: UserSignature) => void;
  onDeleteClick: (id: number) => void;
  isSettingDefault: boolean;
  editSignatureId: number;
  currentUser: CurrentUser;
}

export const SignatureBox: React.FC<Props> = ({
  signature,
  onMakeDefaultClick,
  onEditClick,
  onDeleteClick,
  isSettingDefault,
  editSignatureId,
  currentUser,
}) => {
  return (
    <Box
      sx={{
        borderRadius: 0,
        border: '0.0625rem solid #ECE9DC',
        display: 'flex',
        justifyContent: 'space-between',
        width: 'calc(50% - 1.5rem)',
        padding: '1rem 0.75rem',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography fontWeight={600} fontSize={16} sx={{ lineHeight: '1.25rem', color: '#1D1B20' }}>
          {signature.fullName} {signature?.barNumber && ` ${signature?.barNumber}`}
        </Typography>
        <Spacer h={8} />
        <Typography fontSize={14} sx={{ lineHeight: '1.25rem', color: '#45464F' }}>
          {signature.clientRole}
        </Typography>
        <Spacer h={8} />
        <Typography fontSize={14} sx={{ lineHeight: '1.25rem', color: '#45464F' }}>
          {signature.addressOne}
        </Typography>
        <Spacer h={4} />
        <Typography
          fontSize={14}
          sx={{ lineHeight: '1.25rem', color: '#45464F' }}
        >{`${signature.city}, ${signature.state} ${signature.zipCode}`}</Typography>
        <Spacer h={4} />
        <Typography fontSize={14} sx={{ lineHeight: '1.25rem', color: '#45464F' }}>
          {signature.country}
        </Typography>
        <Spacer h={8} />
        <Typography fontSize={14} sx={{ lineHeight: '1.25rem', color: '#45464F' }}>
          {signature.phone}
        </Typography>
        <Spacer h={4} />
        <Typography fontSize={14} sx={{ lineHeight: '1.25rem', color: '#45464F' }}>
          {signature.email}
        </Typography>
        <Spacer h={8} />
        <Typography fontSize={14} sx={{ lineHeight: '1.25rem', color: '#45464F' }}>
          {signature.fax}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-evenly',
        }}
      >
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <IconButton onClick={() => onMakeDefaultClick(signature)} disabled={signature.isDefault}>
            {signature.isDefault ? <StarIcon sx={{ color: '#436EB2' }} /> : <StarBorderOutlinedIcon />}
          </IconButton>
          {isSettingDefault &&
            editSignatureId === signature.id &&
            Array.from({ length: 6 }).map((_, index) => {
              const angle = (index / 6) * 2 * Math.PI;
              const x = Math.cos(angle) * 30;
              const y = Math.sin(angle) * 30;
              return (
                <div
                  key={index}
                  style={
                    {
                      position: 'absolute',
                      top: '40%',
                      left: '40%',
                      width: '0.625rem',
                      height: '0.625rem',
                      backgroundColor: 'rgb(0, 75, 187)',
                      borderRadius: '50%',
                      pointerEvents: 'none',
                      zIndex: 0,
                      animation: 'particle-burst 1s ease-out forwards',
                      transformOrigin: 'center',
                      '--x-offset': `${x}px`,
                      '--y-offset': `${y}px`,
                    } as React.CSSProperties
                  }
                />
              );
            })}
        </div>
        <IconButton onClick={() => onEditClick(signature)}>
          <CreateOutlinedIcon />
        </IconButton>
        <IconButton
          disabled={currentUser.signatures.length === 1 || signature.isDefault}
          onClick={() => onDeleteClick(signature.id)}
        >
          <DeleteOutlineOutlinedIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
